import {
  type Team,
  TeamsApi,
  type TeamWithCountsDto,
} from '@up2stay/up2promo-api-client';
import { Input, Label, Textarea } from '@up2stay/up2promo-ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { AutosaveStatusCue } from '@/core/components/ui/autosave-status-cue';
import {
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { SettingsSection } from '@/core/components/settings/settings-section';
import { useAutosaveCue } from '@/core/hooks/use-autosave-cue';

import { TEAM_COLOR_PRESETS } from './team-color-presets';

interface TeamInlineEditSectionProps {
  workspaceId: string;
  team: TeamWithCountsDto;
  onUpdated: (team: TeamWithCountsDto) => void;
  dict: {
    generalTitle: string;
    name: string;
    namePlaceholder: string;
    description: string;
    descriptionPlaceholder: string;
    color: string;
    saving: string;
    saveError: string;
    errorNameTaken: string;
  };
}

function teamSnapshot(
  name: string,
  description: string,
  color: string,
): string {
  return JSON.stringify({
    name: name.trim(),
    description: description.trim(),
    color,
  });
}

export function TeamInlineEditSection({
  workspaceId,
  team,
  onUpdated,
  dict,
}: TeamInlineEditSectionProps) {
  const [name, setName] = useState(team.name);
  const [description, setDescription] = useState(team.description ?? '');
  const [color, setColor] = useState(team.color ?? '');
  const {
    status: autosaveStatus,
    markSaving,
    markSaved,
    markIdle,
  } = useAutosaveCue();

  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [lastSynced, setLastSynced] = useState(() =>
    teamSnapshot(team.name, team.description ?? '', team.color ?? ''),
  );

  const teamSyncKey = [
    team.id,
    team.name,
    team.description ?? '',
    team.color ?? '',
  ].join('\0');
  const [prevTeamSyncKey, setPrevTeamSyncKey] = useState(teamSyncKey);
  if (teamSyncKey !== prevTeamSyncKey) {
    setPrevTeamSyncKey(teamSyncKey);
    setName(team.name);
    setDescription(team.description ?? '');
    setColor(team.color ?? '');
    setLastSynced(
      teamSnapshot(team.name, team.description ?? '', team.color ?? ''),
    );
  }

  const persist = useCallback(async () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    const snapshot = teamSnapshot(name, description, color);
    if (snapshot === lastSynced) return;

    markSaving();
    try {
      const res = await TeamsApi.update({
        path: { workspaceId, teamId: team.id },
        body: {
          name: trimmedName,
          description: description.trim() || undefined,
          color: color || undefined,
        },
      });
      const updated = res.data as Team | undefined;
      if (updated) {
        const merged = { ...team, ...updated };
        onUpdated(merged);
        setLastSynced(
          teamSnapshot(
            merged.name,
            merged.description ?? '',
            merged.color ?? '',
          ),
        );
      }
      markSaved();
    } catch (err: unknown) {
      markIdle();
      console.error(err);
      const nameTaken =
        (err as { response?: { data?: { errors?: { name?: string } } } })
          ?.response?.data?.errors?.name === 'teamNameAlreadyExists';
      toast.error(nameTaken ? dict.errorNameTaken : dict.saveError);
    }
  }, [
    color,
    description,
    dict.errorNameTaken,
    dict.saveError,
    markIdle,
    markSaved,
    markSaving,
    name,
    onUpdated,
    lastSynced,
    team,
    workspaceId,
  ]);

  useEffect(() => {
    if (!name.trim()) return;

    const snapshot = teamSnapshot(name, description, color);
    if (snapshot === lastSynced) return;

    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      void persist();
    }, 250);

    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [color, description, lastSynced, name, persist]);

  return (
    <SettingsSection
      id="team-general"
      title={dict.generalTitle}
      grouped
      actions={
        <AutosaveStatusCue
          status={autosaveStatus}
          labels={{ saving: dict.saving }}
        />
      }
    >
      <SettingsGroupRows>
        <SettingsGroupRow>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="team-name">{dict.name}</Label>
            <Input
              id="team-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={dict.namePlaceholder}
              maxLength={80}
            />
          </div>
        </SettingsGroupRow>
        <SettingsGroupRow separated>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="team-description">{dict.description}</Label>
            <Textarea
              id="team-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={dict.descriptionPlaceholder}
              rows={2}
              className="resize-none"
              maxLength={500}
            />
          </div>
        </SettingsGroupRow>
        <SettingsGroupRow separated>
          <div className="flex flex-col gap-2">
            <Label>{dict.color}</Label>
            <div className="flex flex-wrap gap-2">
              {TEAM_COLOR_PRESETS.map((preset) => {
                const isActive = color === preset;
                return (
                  <button
                    key={preset}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => setColor(preset)}
                    className={`size-8 rounded-full border-2 transition ${
                      isActive
                        ? 'scale-110 border-foreground'
                        : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: preset }}
                  />
                );
              })}
              <button
                type="button"
                aria-pressed={!color}
                onClick={() => setColor('')}
                className={`h-8 rounded-full border px-2 text-xs ${
                  !color
                    ? 'border-foreground bg-muted'
                    : 'border-input bg-background'
                }`}
              >
                —
              </button>
            </div>
          </div>
        </SettingsGroupRow>
      </SettingsGroupRows>
    </SettingsSection>
  );
}
